// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { DiagnosticReport, ServiceRequest } from '@medplum/fhirtypes';
import { HSAT_STATUS_LABELS, HSAT_STATUS_TAG_SYSTEM } from '@polarissleep/shared';

export type HsatStatusCode =
  | 'patient-approval-pending'
  | 'hsat-ordered'
  | 'hsat-partial-results'
  | 'hsat-final-results';

export type ResultAvailability = 'Available' | 'Pending';

export interface SleepTestStatus {
  serviceRequest: ServiceRequest;
  orderNumber: string;
  /** HSAT workflow tag code from `HSAT_STATUS_TAG_SYSTEM`, or `null` if the order has no tag yet. */
  orderStatus: HsatStatusCode | null;
  /** Human-readable label for `orderStatus`, falling back to "Pending" when untagged. */
  orderStatusLabel: string;
  resultAvailability: ResultAvailability;
}

/**
 * Reads the HSAT workflow status tag the provider app writes to `ServiceRequest.meta.tag`
 * (system = `HSAT_STATUS_TAG_SYSTEM`). This is the same source of truth the provider
 * HSAT Devices page uses, so patient and provider views never disagree.
 */
function extractHsatStatus(sr: ServiceRequest): HsatStatusCode | null {
  const tag = sr.meta?.tag?.find((t) => t.system === HSAT_STATUS_TAG_SYSTEM);
  return (tag?.code as HsatStatusCode | undefined) ?? null;
}

function deriveResultAvailability(
  sr: ServiceRequest,
  reports: DiagnosticReport[],
  status: HsatStatusCode | null
): ResultAvailability {
  if (status === 'hsat-partial-results' || status === 'hsat-final-results') {
    return 'Available';
  }
  const srRef = `ServiceRequest/${sr.id}`;
  const hasLinkedReport = reports.some((report) => report.basedOn?.some((ref) => ref.reference === srRef));
  return hasLinkedReport ? 'Available' : 'Pending';
}

function getOrderNumber(sr: ServiceRequest): string {
  const declared = sr.identifier?.[0]?.value;
  if (declared) {
    return declared;
  }
  return `PAT${(sr.id ?? '').slice(-9).toUpperCase()}`;
}

/**
 * Synchronous derivation: pulls the most recent HSAT ServiceRequest the
 * dashboard already loads via `useMyChartData` and turns it into the panel's
 * two status cells. Returns `null` when the patient has no HSAT order yet
 * (the panel renders the empty state).
 */
export function deriveSleepTestStatus(
  hsatOrders: ServiceRequest[],
  hsatReports: DiagnosticReport[]
): SleepTestStatus | null {
  if (hsatOrders.length === 0) {
    return null;
  }
  const sr = hsatOrders[0];
  const orderStatus = extractHsatStatus(sr);
  return {
    serviceRequest: sr,
    orderNumber: getOrderNumber(sr),
    orderStatus,
    orderStatusLabel: orderStatus ? HSAT_STATUS_LABELS[orderStatus] : 'Pending',
    resultAvailability: deriveResultAvailability(sr, hsatReports, orderStatus),
  };
}
